import { useState, useEffect, useCallback, useRef } from 'react';
import api from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Plus, Pencil, Trash2, Download, Building2, Upload, FileText } from 'lucide-react';
import { toast } from 'sonner';
import SearchBar from '../components/SearchBar';

const MONTHS = ['01','02','03','04','05','06','07','08','09','10','11','12'];
const MONTH_NAMES = ['January','February','March','April','May','June','July','August','September','October','November','December'];

export default function RentTrackerPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [tenants, setTenants] = useState([]);
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tenantDialog, setTenantDialog] = useState(false);
  const [paymentDialog, setPaymentDialog] = useState(false);
  const [editId, setEditId] = useState(null);
  const [monthFilter, setMonthFilter] = useState(String(new Date().getMonth() + 1).padStart(2, '0'));
  const [yearFilter, setYearFilter] = useState(String(new Date().getFullYear()));
  const [searchText, setSearchText] = useState('');
  const [tenantForm, setTenantForm] = useState({ unit_number: '', name: '', contact: '', rent_amount: 0, due_date: '1', upi_id: '' });
  const [paymentForm, setPaymentForm] = useState({ tenant_id: '', tenant_name: '', unit_number: '', month: '', year: '', amount: 0, status: 'Pending', payment_date: '', notes: '' });
  const [paymentFile, setPaymentFile] = useState(null);
  const paymentFileRef = useRef(null);

  const fetchData = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (monthFilter) params.set('month', monthFilter);
      if (yearFilter) params.set('year', yearFilter);
      if (searchText) params.set('search', searchText);
      const [tRes, pRes] = await Promise.all([api.get(`/rent/tenants?search=${searchText}`), api.get(`/rent/payments?${params}`)]);
      setTenants(tRes.data);
      setPayments(pRes.data);
    } catch { toast.error('Failed to load data'); }
    finally { setLoading(false); }
  }, [monthFilter, yearFilter, searchText]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Tenant CRUD
  const openNewTenant = () => { setEditId(null); setTenantForm({ unit_number: '', name: '', contact: '', rent_amount: 0, due_date: '1', upi_id: '' }); setTenantDialog(true); };
  const openEditTenant = (t) => { setEditId(t._id); setTenantForm({ unit_number: t.unit_number, name: t.name, contact: t.contact || '', rent_amount: t.rent_amount, due_date: t.due_date || '1', upi_id: t.upi_id || '' }); setTenantDialog(true); };

  const saveTenant = async () => {
    if (!tenantForm.name || !tenantForm.unit_number) { toast.error('Name and unit required'); return; }
    try {
      if (editId) { await api.put(`/rent/tenants/${editId}`, tenantForm); toast.success('Updated'); }
      else { await api.post('/rent/tenants', tenantForm); toast.success('Added'); }
      setTenantDialog(false); fetchData();
    } catch { toast.error('Failed to save'); }
  };

  const deleteTenant = async (id) => {
    if (!window.confirm('Delete tenant?')) return;
    try { await api.delete(`/rent/tenants/${id}`); toast.success('Deleted'); fetchData(); } catch { toast.error('Failed'); }
  };

  // Payment CRUD
  const openNewPayment = () => {
    setEditId(null);
    setPaymentForm({ tenant_id: '', tenant_name: '', unit_number: '', month: monthFilter, year: yearFilter, amount: 0, status: 'Pending', payment_date: '', notes: '' });
    setPaymentFile(null);
    if (paymentFileRef.current) paymentFileRef.current.value = '';
    setPaymentDialog(true);
  };
  const openEditPayment = (p) => {
    setEditId(p._id);
    setPaymentForm({ tenant_id: p.tenant_id, tenant_name: p.tenant_name, unit_number: p.unit_number, month: p.month, year: p.year, amount: p.amount, status: p.status, payment_date: p.payment_date || '', notes: p.notes || '' });
    setPaymentFile(null);
    if (paymentFileRef.current) paymentFileRef.current.value = '';
    setPaymentDialog(true);
  };

  const uploadPaymentDoc = async (paymentId, file) => {
    const fd = new FormData();
    fd.append('file', file);
    await api.post(`/rent/payments/${paymentId}/upload`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
  };

  const savePayment = async () => {
    if (!paymentForm.tenant_id || !paymentForm.month) { toast.error('Tenant and month required'); return; }
    try {
      let paymentId = editId;
      if (editId) { await api.put(`/rent/payments/${editId}`, paymentForm); toast.success('Updated'); }
      else {
        const res = await api.post('/rent/payments', paymentForm);
        paymentId = res.data.id;
        toast.success('Recorded');
      }
      if (paymentFile && paymentId) {
        try { await uploadPaymentDoc(paymentId, paymentFile); toast.success('Screenshot attached'); }
        catch { toast.error('Payment saved but screenshot upload failed'); }
      }
      setPaymentDialog(false);
      setPaymentFile(null);
      fetchData();
    } catch { toast.error('Failed to save'); }
  };

  const downloadPaymentDoc = async (paymentId, docIndex, filename) => {
    try {
      const res = await api.get(`/rent/payments/${paymentId}/documents/${docIndex}`, { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a'); a.href = url; a.download = filename || 'rent_receipt'; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error('Download failed'); }
  };

  const uploadToExisting = async (payment, file) => {
    try { await uploadPaymentDoc(payment._id, file); toast.success('Screenshot attached'); fetchData(); }
    catch { toast.error('Upload failed'); }
  };

  const handleExport = async () => {
    try {
      const response = await api.get(`/export/rent?year=${yearFilter}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const a = document.createElement('a'); a.href = url; a.download = 'rent_export.csv'; a.click();
    } catch { toast.error('Export failed'); }
  };

  const selectTenant = (tid) => {
    const t = tenants.find(tn => tn._id === tid);
    if (t) setPaymentForm(prev => ({ ...prev, tenant_id: tid, tenant_name: t.name, unit_number: t.unit_number, amount: t.rent_amount }));
  };

  return (
    <div data-testid="rent-tracker-page">
      <div className="flex items-center gap-3 mb-6">
        <Building2 size={28} className="text-[#D4AF37]" />
        <div>
          <h1 className="font-heading text-4xl font-semibold text-[#111B11] tracking-tight">Rent Tracker</h1>
          <p className="text-sm text-[#4F734F] mt-1">Adishakthi Royal Grove - Rent Collection</p>
        </div>
      </div>

      <Tabs defaultValue="payments" className="space-y-4">
        <div className="flex items-center justify-between">
          <TabsList>
            <TabsTrigger value="payments" data-testid="tab-payments">Payments</TabsTrigger>
            <TabsTrigger value="tenants" data-testid="tab-tenants">Tenants</TabsTrigger>
          </TabsList>
          <div className="flex gap-2">
            <Button onClick={handleExport} variant="outline" size="sm" className="gap-1" data-testid="export-rent-btn"><Download size={14} /> Export</Button>
          </div>
        </div>

        <TabsContent value="payments">
          <div className="flex flex-wrap items-center gap-3 mb-4">
            <SearchBar
              value={searchText}
              onChange={setSearchText}
              placeholder="Search tenant, unit..."
              testId="rent-search"
              minWidth="min-w-[260px]"
              className="!flex-none w-full sm:w-72"
            />
            <Select value={monthFilter} onValueChange={setMonthFilter}>
              <SelectTrigger className="w-40 h-11 input-field"><SelectValue /></SelectTrigger>
              <SelectContent>{MONTHS.map((m, i) => <SelectItem key={m} value={m}>{MONTH_NAMES[i]}</SelectItem>)}</SelectContent>
            </Select>
            <Select value={yearFilter} onValueChange={setYearFilter}>
              <SelectTrigger className="w-32 h-11 input-field"><SelectValue /></SelectTrigger>
              <SelectContent>{['2024','2025','2026'].map(y => <SelectItem key={y} value={y}>{y}</SelectItem>)}</SelectContent>
            </Select>
            <Button onClick={openNewPayment} className="btn-accent gap-2 ml-auto" data-testid="add-payment-btn"><Plus size={16} /> Record Payment</Button>
          </div>

          <div className="bg-white border border-[#E6EDE6] rounded-sm overflow-hidden">
            <table className="data-table w-full">
              <thead><tr><th>Tenant</th><th>Unit</th><th>Amount</th><th>Status</th><th>Payment Date</th><th>Receipt</th><th>Actions</th></tr></thead>
              <tbody>
                {loading ? <tr><td colSpan={7} className="text-center py-8 text-olive-400">Loading...</td></tr> :
                payments.length === 0 ? <tr><td colSpan={7} className="text-center py-8 text-olive-400">No payments found</td></tr> :
                payments.map(p => (
                  <tr key={p._id}>
                    <td className="font-medium text-[#111B11]">{p.tenant_name}</td>
                    <td>{p.unit_number}</td>
                    <td className="font-mono">{p.amount?.toLocaleString('en-IN')}</td>
                    <td><span className={`rounded-full px-2 py-0.5 text-xs font-medium ${p.status === 'Paid' ? 'bg-green-100 text-green-700' : p.status === 'Partial' ? 'bg-yellow-100 text-yellow-700' : 'bg-red-100 text-red-700'}`}>{p.status}</span></td>
                    <td>{p.payment_date || '-'}</td>
                    <td>
                      <div className="flex items-center gap-1 flex-wrap">
                        {(p.documents || []).map((doc, i) => (
                          <button key={i} onClick={() => downloadPaymentDoc(p._id, i, doc.filename)} className="p-1 hover:bg-olive-50 rounded-sm" title={doc.filename} data-testid={`rent-doc-${p._id}-${i}`}>
                            <FileText size={13} className="text-[#D4AF37]" />
                          </button>
                        ))}
                        <label className="p-1 hover:bg-olive-50 rounded-sm cursor-pointer" title="Upload screenshot">
                          <Upload size={13} className="text-olive-500" />
                          <input type="file" accept="image/*,application/pdf" className="hidden" data-testid={`rent-upload-${p._id}`} onChange={(ev) => { const f = ev.target.files?.[0]; if (f) uploadToExisting(p, f); ev.target.value = ''; }} />
                        </label>
                      </div>
                    </td>
                    <td><button onClick={() => openEditPayment(p)} className="p-1.5 hover:bg-olive-50 rounded-sm"><Pencil size={14} className="text-olive-500" /></button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="tenants">
          <div className="flex justify-end mb-4">
            {isAdmin && <Button onClick={openNewTenant} className="btn-accent gap-2" data-testid="add-tenant-btn"><Plus size={16} /> Add Tenant</Button>}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {tenants.map(t => (
              <div key={t._id} className="bg-white border border-[#E6EDE6] rounded-sm p-5" data-testid={`tenant-card-${t._id}`}>
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <p className="font-medium text-[#111B11]">{t.name}</p>
                    <p className="text-xs text-olive-500">Unit {t.unit_number}</p>
                  </div>
                  {isAdmin && (
                    <div className="flex gap-1">
                      <button onClick={() => openEditTenant(t)} className="p-1 hover:bg-olive-50 rounded-sm"><Pencil size={13} className="text-olive-500" /></button>
                      <button onClick={() => deleteTenant(t._id)} className="p-1 hover:bg-red-50 rounded-sm"><Trash2 size={13} className="text-red-500" /></button>
                    </div>
                  )}
                </div>
                <div className="space-y-1 text-sm">
                  <div className="flex justify-between"><span className="text-olive-500">Rent</span><span className="font-mono font-medium text-[#111B11]">{t.rent_amount?.toLocaleString('en-IN')}</span></div>
                  <div className="flex justify-between"><span className="text-olive-500">Due Date</span><span>{t.due_date}th of month</span></div>
                  {t.contact && <div className="flex justify-between"><span className="text-olive-500">Contact</span><span>{t.contact}</span></div>}
                  {t.upi_id && <div className="flex justify-between"><span className="text-olive-500">UPI</span><span className="font-mono text-xs">{t.upi_id}</span></div>}
                </div>
              </div>
            ))}
            {tenants.length === 0 && <p className="text-sm text-olive-400 col-span-3 text-center py-8">No tenants added yet</p>}
          </div>
        </TabsContent>
      </Tabs>

      {/* Tenant Dialog */}
      <Dialog open={tenantDialog} onOpenChange={setTenantDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="font-heading text-xl">{editId ? 'Edit' : 'Add'} Tenant</DialogTitle></DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5"><Label className="text-xs uppercase font-semibold text-[#4F734F]">Unit Number</Label><Input value={tenantForm.unit_number} onChange={(e) => setTenantForm(prev => ({ ...prev, unit_number: e.target.value }))} className="input-field" data-testid="tenant-unit-input" /></div>
              <div className="space-y-1.5"><Label className="text-xs uppercase font-semibold text-[#4F734F]">Name</Label><Input value={tenantForm.name} onChange={(e) => setTenantForm(prev => ({ ...prev, name: e.target.value }))} className="input-field" data-testid="tenant-name-input" /></div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5"><Label className="text-xs uppercase font-semibold text-[#4F734F]">Contact</Label><Input value={tenantForm.contact} onChange={(e) => setTenantForm(prev => ({ ...prev, contact: e.target.value }))} className="input-field" /></div>
              <div className="space-y-1.5"><Label className="text-xs uppercase font-semibold text-[#4F734F]">Rent Amount</Label><Input type="number" value={tenantForm.rent_amount} onChange={(e) => setTenantForm(prev => ({ ...prev, rent_amount: Number(e.target.value) }))} className="input-field" /></div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5"><Label className="text-xs uppercase font-semibold text-[#4F734F]">Due Date (day)</Label><Input value={tenantForm.due_date} onChange={(e) => setTenantForm(prev => ({ ...prev, due_date: e.target.value }))} className="input-field" /></div>
              <div className="space-y-1.5"><Label className="text-xs uppercase font-semibold text-[#4F734F]">UPI ID</Label><Input value={tenantForm.upi_id} onChange={(e) => setTenantForm(prev => ({ ...prev, upi_id: e.target.value }))} className="input-field" /></div>
            </div>
            <div className="flex gap-3 pt-2">
              <Button onClick={saveTenant} className="btn-primary" data-testid="save-tenant-btn">{editId ? 'Update' : 'Add'}</Button>
              <Button variant="outline" onClick={() => setTenantDialog(false)}>Cancel</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Payment Dialog */}
      <Dialog open={paymentDialog} onOpenChange={setPaymentDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="font-heading text-xl">{editId ? 'Edit' : 'Record'} Payment</DialogTitle></DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="space-y-1.5">
              <Label className="text-xs uppercase font-semibold text-[#4F734F]">Tenant</Label>
              <Select value={paymentForm.tenant_id || 'none'} onValueChange={selectTenant}>
                <SelectTrigger className="input-field"><SelectValue placeholder="Select tenant" /></SelectTrigger>
                <SelectContent><SelectItem value="none" disabled>Select</SelectItem>{tenants.map(t => <SelectItem key={t._id} value={t._id}>{t.name} - Unit {t.unit_number}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5"><Label className="text-xs uppercase font-semibold text-[#4F734F]">Month</Label>
                <Select value={paymentForm.month} onValueChange={(v) => setPaymentForm(prev => ({ ...prev, month: v }))}>
                  <SelectTrigger className="input-field"><SelectValue /></SelectTrigger>
                  <SelectContent>{MONTHS.map((m, i) => <SelectItem key={m} value={m}>{MONTH_NAMES[i]}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5"><Label className="text-xs uppercase font-semibold text-[#4F734F]">Year</Label>
                <Select value={paymentForm.year} onValueChange={(v) => setPaymentForm(prev => ({ ...prev, year: v }))}>
                  <SelectTrigger className="input-field"><SelectValue /></SelectTrigger>
                  <SelectContent>{['2024','2025','2026'].map(y => <SelectItem key={y} value={y}>{y}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5"><Label className="text-xs uppercase font-semibold text-[#4F734F]">Amount</Label><Input type="number" value={paymentForm.amount} onChange={(e) => setPaymentForm(prev => ({ ...prev, amount: Number(e.target.value) }))} className="input-field" /></div>
              <div className="space-y-1.5"><Label className="text-xs uppercase font-semibold text-[#4F734F]">Status</Label>
                <Select value={paymentForm.status} onValueChange={(v) => setPaymentForm(prev => ({ ...prev, status: v }))}>
                  <SelectTrigger className="input-field"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="Pending">Pending</SelectItem><SelectItem value="Paid">Paid</SelectItem><SelectItem value="Partial">Partial</SelectItem></SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-1.5"><Label className="text-xs uppercase font-semibold text-[#4F734F]">Payment Date</Label><Input type="date" value={paymentForm.payment_date} onChange={(e) => setPaymentForm(prev => ({ ...prev, payment_date: e.target.value }))} className="input-field" /></div>
            <div className="space-y-1.5"><Label className="text-xs uppercase font-semibold text-[#4F734F]">Notes</Label><Textarea value={paymentForm.notes} onChange={(e) => setPaymentForm(prev => ({ ...prev, notes: e.target.value }))} className="input-field" /></div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase font-semibold text-[#4F734F]">Payment Screenshot / Acknowledgement (optional)</Label>
              <Input
                ref={paymentFileRef}
                type="file"
                accept="image/*,application/pdf"
                onChange={(e) => setPaymentFile(e.target.files?.[0] || null)}
                className="input-field"
                data-testid="rent-payment-upload"
              />
              {paymentFile && <p className="text-xs text-[#4F734F]">Selected: {paymentFile.name}</p>}
            </div>
            <div className="flex gap-3 pt-2">
              <Button onClick={savePayment} className="btn-primary" data-testid="save-payment-btn">{editId ? 'Update' : 'Record'}</Button>
              <Button variant="outline" onClick={() => setPaymentDialog(false)}>Cancel</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
